import { useTranslate } from '@refinedev/core';
import { Form, Radio, InputNumber, Divider } from 'antd';
import { CollectionData } from '@/api/generated/schemas';
import { CollectionQueryBuilder } from './CollectionQueryBuilder';
import { CollectionSortingList } from './CollectionSortingList';

interface Props {
  /** Sets the create-time default for `collection_data`. */
  mode: 'create' | 'edit';
  /** Disable the whole section (e.g. system-managed records). */
  disabled?: boolean;
}

/**
 * Shared form section for `collection_data` + dynamic-collection configuration
 * (query builder, sorting, limit). Used by categories and managed_collections.
 */
export const CollectionDataFields = ({ mode, disabled }: Props) => {
  const t = useTranslate();
  const form = Form.useFormInstance();
  const collectionData = Form.useWatch('collection_data', form);
  const isDynamic = collectionData === CollectionData.dynamic;

  return (
    <>
      <Divider orientation="left">
        {t('base.collection.collection_data')}
      </Divider>

      <Form.Item
        name="collection_data"
        label={t('base.collection.collection_data')}
        initialValue={mode === 'create' ? CollectionData.static : undefined}
      >
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          disabled={disabled}
          options={Object.values(CollectionData).map((value) => ({
            label: t(`base.collection.data.${value}`),
            value,
          }))}
        />
      </Form.Item>

      {isDynamic && (
        <Form.Item
          name="publication_custom_filters"
          label={t('base.collection.filters')}
        >
          <CollectionQueryBuilder disabled={disabled} />
        </Form.Item>
      )}

      {/* Outside the dynamic block: a static collection honours its stored
          order too, so hiding this would make it invisible and uneditable. */}
      <Form.Item
        label={t('base.collection.sorting')}
        extra={t('base.collection.sorting_hint')}
      >
        <CollectionSortingList disabled={disabled} />
      </Form.Item>

      {isDynamic && (
        <Form.Item
          name="publication_custom_limit"
          label={t('base.collection.limit')}
          extra={t('base.collection.limit_hint')}
        >
          <InputNumber min={1} disabled={disabled} style={{ width: 200 }} />
        </Form.Item>
      )}
    </>
  );
};
